import { Button, CircularProgress } from "@mui/material";
import { ReactComponent as LineSvg } from "assets/line.svg";
import Icon from "components/icon/icon";
import Results from "components/results/results";
import { RightPanel } from "helpers/constants";
import { AppAction, AppContext } from "helpers/context";
import { handleGetLineResultGroupData } from "helpers/groupResultData";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import generate from "report/generation";
import getDoc from "report/line/document";
import styles from "./line-analysed.module.scss";

function LineAnalysed() {
  const [context, dispatch] = useContext(AppContext);
  const [t] = useTranslation();
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const selectedPolyline = context.selectedShapeId
    ? context.shapes.find((pl) => pl.id === context.selectedShapeId)
    : undefined;
  const activeShape = selectedPolyline || (context.currentShape as any);
  const activeShapeId = (activeShape as any)?.id;
  const isLineResultOwner =
    !!activeShapeId && context.lineAnalysisShapeId === activeShapeId;
  const canShowLineResult =
    isLineResultOwner &&
    (context.lineAnalysisStatus === "running" ||
      context.lineAnalysisStatus === "error" ||
      (!!context.lineAnalysisResult && !!context.lineAnalysisLegends));

  //   const locationsTable = handleFilterLocations({
  //     data: currentLocationSet?.items || [],
  //     unit:
  //       currentLocationSet?.aggregationUnit ?? "numberOfLocations",
  //     multipleLocationsSearch: context.multipleLocationsSearch,
  //     locationsFilterInfo: context.locationsFilterInfo,
  //     filterChecked: true
  //   });

  const getShapeType = () => {
    const activeShape = selectedPolyline || context.currentShape;
    if (!activeShape) return "";

    let type = "Ground";
    const cs = activeShape as any;

    if (selectedPolyline?.type) {
      type = selectedPolyline.type;
      return t(`myShapes.type-item.${type}`);
    }

    // Check if it's DrawShapeData
    if (cs.type !== undefined && cs.coordinates !== undefined) {
      type = cs.type || "Ground";
    } else if (cs.__type) {
      // Legacy support for old shape objects
      type = cs.__type;
    } else if (cs.__isCheckboxShape) {
      const shapeId = cs.__shapeId;
      const shape = context.myShapes?.find((s: any) => s.id === shapeId);
      if (shape) type = shape.type || "Ground";
    } else if (cs.shapeId) {
      // New DrawShapeData with shapeId reference
      const shape = context.myShapes?.find((s: any) => s.id === cs.shapeId);
      if (shape) type = shape.type || "Ground";
    }

    return t(`myShapes.type-item.${type}`);
  };

  const getShapeName = () => {
    const activeShape = selectedPolyline || context.currentShape;
    if (!activeShape) return "";
    const cs = activeShape as any;

    if (selectedPolyline?.name) {
      return selectedPolyline.name;
    }

    // If currentShape is DrawShapeData
    if (cs.id && cs.coordinates && cs.source !== undefined) {
      return (
        cs.name ||
        t("drawShape.lineInfoTitle", {
          data: (context.shapes.length ?? 0) + 1
        })
      );
    }

    // Legacy support for old shape objects
    if (cs.__name) return cs.__name;

    if (context.selectedShapeId) {
      const selectedPolyline = context.shapes.find(
        (pl) => pl.id === context.selectedShapeId
      );
      if (selectedPolyline) {
        return selectedPolyline.name;
      }
    }

    return t("drawShape.lineInfoTitle", {
      data: (context.shapes.length ?? 0) + 1
    });
  };

  const openModal = (): void => {
    dispatch({ type: AppAction.setAnalyseShapeModal, payload: true });
  };

  const handleDownloadPdf = async () => {
    if (
      !context.lineAnalysisResult ||
      !context.lineAnalysisLegends ||
      context.lineAnalysisStatus === "running" ||
      context.lineAnalysisStatus === "error"
    ) {
      return;
    }

    setIsPdfLoading(true);
    try {
      const { distanceData, extremeMap } = handleGetLineResultGroupData({
        resultData: context.lineAnalysisResult,
        legendData: context.lineAnalysisLegends,
        needChartData: false
      });

      const shapeName = getShapeName();
      const lineLength = handleDistance() || "";
      const projectType = getShapeType();
      const fileName =
        shapeName ||
        t("drawShape.lineInfoTitle", {
          data: (context.shapes.length ?? 0) + 1
        });

      const docDef = await getDoc({
        t,
        locationSet: {
          id: "",
          name: fileName,
          total: 0,
          lat: 0,
          lon: 0,
          checked: false,
          locationSource: "",
          aggregationUnit: "numberOfLocations",
          itemCount: context.lineAnalysisResult.length
        },
        lineName: fileName,
        lineLength,
        projectType,
        analysisItems: context.lineAnalysisResult,
        legendItems: context.lineAnalysisLegends,
        currency: "",
        aggregationUnits: ["numberOfLocations"],
        unit: "numberOfLocations",
        locationsFilterInfo: [],
        lineSeries: distanceData,
        extremeMap,
        selectedShapeId: context.selectedShapeId
      });

      await generate(docDef, `${fileName}.pdf`);

      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          title: "reportGenerated",
          message: "setReportLinerSuccess"
        }
      });
    } catch (e) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          title: "downloadPdfTitle",
          titleIconColor: "yellow",
          titleIcon: "warning",
          resMessage: t("snackBar.setReportLinerFailed")
        }
      });
    } finally {
      setIsPdfLoading(false);
    }
  };

  const closeRightPanel = (): void => {
    goBlack();
  };

  const goBlack = () => {
    // 用户主动关闭线分析面板，标记为不可见
    dispatch({ type: AppAction.setLineAnalysisPanelVisible, payload: false });
    // 清除选中的形状状态
    dispatch({ type: AppAction.deselectShape });
    dispatch({ type: AppAction.setCurrentShape, payload: undefined });
    if (context.currentMultipleLocations) {
      dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Multiple });
    } else {
      dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Empty });
    }
  };

  const handleDistance = () => {
    const shapeObj =
      (selectedPolyline as any)?.aMapRef ||
      (context.currentShape as any)?.aMapRef ||
      (selectedPolyline as any) ||
      (context.currentShape as any);
    if (!shapeObj?.getPath) {
      return "";
    }
    let path = shapeObj.getPath();

    let total = 0;
    for (let i = 0; i < path.length - 1; i++) {
      total += AMap.GeometryUtil.distance(path[i], path[i + 1]);
    }

    if (total < 1000) {
      return t("map.length(m)", { data: total.toFixed(3) });
    } else {
      return t("map.length(km)", { data: (total / 1000).toFixed(3) });
    }
  };

  return (
    <div className={styles.lineAnalysed}>
      <div className="overflow-auto flex-1">
        {context.currentShape && (
          <div className={styles.title}>
            <div className="flex items-center">
              {context.currentMultipleLocations && (
                <Icon icon="arrow_back" onClick={goBlack} />
              )}
              <div className="truncate">{getShapeName()}</div>
            </div>
            <div className="flex items-center">
              <div>
                <Icon onClick={closeRightPanel} />
              </div>
            </div>
          </div>
        )}
        {context.currentShape && (
          <div className={styles.titleInfo}>
            <div className={styles.image}>
              <LineSvg className="w-[30px] h-[30px]" />
            </div>
            <div className={styles.address}>
              <div className="flex justify-between">
                <div style={{ marginRight: "5px" }}>
                  {t("drawShape.length")}:
                </div>
                <div style={{ color: "#000000de", fontSize: "13px" }}>
                  {handleDistance()}
                </div>
              </div>
              <div className="flex justify-between">
                <div style={{ marginRight: "5px" }}>
                  {t("drawShape.projectType")}:
                </div>
                <div style={{ color: "#000000de", fontSize: "13px" }}>
                  {getShapeType()}
                </div>
              </div>
            </div>
          </div>
        )}
        {canShowLineResult && <Results />}
      </div>
      <div className={styles.buttons}>
        {canShowLineResult &&
          context.lineAnalysisResult &&
          context.lineAnalysisLegends && (
            <div className="w-fit mr-3">
              <Button
                variant="outlined"
                color="success"
                disabled={isPdfLoading}
                endIcon={
                  isPdfLoading && <CircularProgress color="success" size={24} />
                }
                onClick={handleDownloadPdf}
              >
                {t("buttons.download")}
              </Button>
            </div>
          )}
        <div className="flex-1">
          <Button
            onClick={openModal}
            variant="contained"
            color="success"
            style={{ width: "100%" }}
          >
            {t("buttons.analyseSelected")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default LineAnalysed;
